// src/pages/CRM/ActivityComm/TaskDetail.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, IconButton,
  CircularProgress, Alert, Divider, Stack, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Checkbox,
  List, ListItem, ListItemText, ListItemIcon, Link as MuiLink
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Repeat as RecurrenceIcon,
  Notifications as ReminderIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as InAppIcon,
  Warning as OverdueIcon,
  SubdirectoryArrowRight as SubtaskIcon,
  LocalOffer as TagIcon,
  Link as LinkIcon,
  TrendingUp as OpportunityIcon,
  ContactPhone as LeadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { taskService } from './activityCommService';
import {
  TaskStatusLabels, TaskStatusColors,
  TaskPriorityLabels, TaskPriorityColors,
  RecurrenceFrequencyLabels,
  ReminderTypeLabels, ReminderStatusLabels, ReminderStatusColors,
  formatDate, formatDateTime, isOverdue
} from './activityCommEnums';
import TaskForm from './TaskForm';

const REMINDER_TYPE_ICONS = {
  EMAIL: EmailIcon,
  SMS: SmsIcon,
  IN_APP: InAppIcon
};

const TaskDetail = () => {
  const { chatbotId, taskId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    document.title = 'Task Details | Activity';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView && taskId) {
      loadTask();
    }
  }, [canView, taskId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadTask = async () => {
    setLoading(true);
    try {
      const response = await taskService.getById(taskId);
      if (response.success) {
        setTask(response.data);
        document.title = `${response.data.title} | Task Detail`;
      } else {
        enqueueSnackbar('Failed to load task', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to load task', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await taskService.complete(taskId);
      if (response.success) {
        setTask(response.data);
        enqueueSnackbar('Task marked as completed', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to complete task', { variant: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await taskService.delete(taskId);
      if (response.success) {
        enqueueSnackbar('Task deleted successfully', { variant: 'success' });
        navigate(`/chatbot/${chatbotId}/crm/activity/tasks`);
      }
    } catch (err) {
      enqueueSnackbar('Failed to delete task', { variant: 'error' });
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleChecklistToggle = async (index) => {
    if (!task?.checklist || !canModify) return;
    const updatedChecklist = task.checklist.map((item, i) =>
      i === index ? { ...item, completed: !item.completed } : item
    );
    try {
      const response = await taskService.update(taskId, { checklist: updatedChecklist });
      if (response.success) {
        setTask(response.data);
      }
    } catch (err) {
      enqueueSnackbar('Failed to update checklist', { variant: 'error' });
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      const response = await taskService.deleteReminder(taskId, reminderId);
      if (response.success) {
        loadTask();
        enqueueSnackbar('Reminder deleted', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to delete reminder', { variant: 'error' });
    }
  };

  const handleEditClose = (refresh) => {
    setEditOpen(false);
    if (refresh) {
      loadTask();
    }
  };

  const taskIsOverdue = task ? isOverdue(task.due_date, task.status) : false;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this task.
      </Alert>
    );
  }

  if (!task) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        Task not found
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/tasks`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {task.title}
              </Typography>
              <Chip
                label={TaskStatusLabels[task.status] || task.status}
                color={TaskStatusColors[task.status] || 'default'}
              />
              <Chip
                label={TaskPriorityLabels[task.priority] || task.priority}
                color={TaskPriorityColors[task.priority] || 'default'}
                variant="outlined"
              />
              {taskIsOverdue && (
                <Chip
                  icon={<OverdueIcon />}
                  label="Overdue"
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        {canModify && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            {task.status !== 'COMPLETED' && (
              <Button
                variant="contained"
                size="small"
                color="success"
                startIcon={<CompleteIcon />}
                onClick={handleComplete}
                disabled={completing}
              >
                {completing ? 'Completing...' : 'Complete'}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </Stack>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Details Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {task.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {task.description}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Due Date</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color={taskIsOverdue ? 'error' : 'action'} />
                    <Typography
                      variant="body2"
                      color={taskIsOverdue ? 'error.main' : 'text.primary'}
                      fontWeight={taskIsOverdue ? 600 : 400}
                    >
                      {task.due_date ? formatDateTime(task.due_date) : '-'}
                    </Typography>
                    {taskIsOverdue && (
                      <Chip label="Overdue" color="error" size="small" />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography variant="body2">
                    {task.start_date ? formatDateTime(task.start_date) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Created At</Typography>
                  <Typography variant="body2">{formatDateTime(task.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Updated At</Typography>
                  <Typography variant="body2">{formatDateTime(task.updated_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {task.assigned_user_name || 'Unassigned'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Linked Records */}
            {(task.contact || task.account || task.lead || task.opportunity) && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Linked Records
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {task.contact && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">Contact</Typography>
                      </Box>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${task.contact.id}`)}
                        sx={{ mt: 0.5, cursor: 'pointer' }}
                      >
                        {task.contact.full_name || task.contact.name || `Contact #${task.contact.id}`}
                      </MuiLink>
                    </Grid>
                  )}
                  {task.account && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="secondary" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">Account</Typography>
                      </Box>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/accounts/${task.account.id}`)}
                        sx={{ mt: 0.5, cursor: 'pointer' }}
                      >
                        {task.account.name || `Account #${task.account.id}`}
                      </MuiLink>
                    </Grid>
                  )}
                  {task.lead && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LeadIcon color="info" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">Lead</Typography>
                      </Box>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads/${task.lead.id}`)}
                        sx={{ mt: 0.5, cursor: 'pointer' }}
                      >
                        {task.lead.full_name || task.lead.name || `Lead #${task.lead.id}`}
                      </MuiLink>
                    </Grid>
                  )}
                  {task.opportunity && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OpportunityIcon color="success" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">Opportunity</Typography>
                      </Box>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/sfa/opportunities/${task.opportunity.id}`)}
                        sx={{ mt: 0.5, cursor: 'pointer' }}
                      >
                        {task.opportunity.name || `Opportunity #${task.opportunity.id}`}
                      </MuiLink>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Checklist Section */}
            {task.checklist && task.checklist.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Checklist ({task.checklist.filter(i => i.completed).length}/{task.checklist.length})
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <List dense disablePadding>
                  {task.checklist.map((item, index) => (
                    <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={!!item.completed}
                          onChange={() => handleChecklistToggle(index)}
                          disabled={!canModify}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text || item.label || item.title}
                        primaryTypographyProps={{
                          sx: {
                            textDecoration: item.completed ? 'line-through' : 'none',
                            color: item.completed ? 'text.secondary' : 'text.primary'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Subtasks Section */}
            {task.subtasks && task.subtasks.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Subtasks ({task.subtasks.length})
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <List dense disablePadding>
                  {task.subtasks.map((subtask) => (
                    <ListItem
                      key={subtask.id}
                      disableGutters
                      sx={{
                        py: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1
                      }}
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/tasks/${subtask.id}`)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <SubtaskIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={subtask.title}
                        secondary={subtask.due_date ? `Due: ${formatDate(subtask.due_date)}` : 'No due date'}
                      />
                      <Chip
                        label={TaskStatusLabels[subtask.status] || subtask.status}
                        color={TaskStatusColors[subtask.status] || 'default'}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Reminders */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Reminders
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {(!task.reminders || task.reminders.length === 0) ? (
                <Typography variant="body2" color="text.secondary">No reminders set</Typography>
              ) : (
                <List dense disablePadding>
                  {task.reminders.map((reminder) => {
                    const TypeIcon = REMINDER_TYPE_ICONS[reminder.reminder_type] || ReminderIcon;
                    return (
                      <ListItem
                        key={reminder.id}
                        disableGutters
                        secondaryAction={
                          canModify && (
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDeleteReminder(reminder.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )
                        }
                        sx={{ py: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <TypeIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {ReminderTypeLabels[reminder.reminder_type] || reminder.reminder_type}
                              </Typography>
                              <Chip
                                label={ReminderStatusLabels[reminder.status] || reminder.status}
                                color={ReminderStatusColors[reminder.status] || 'default'}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={formatDateTime(reminder.remind_at)}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>

            {/* Recurrence Info */}
            {task.is_recurring && task.recurrence && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <RecurrenceIcon color="primary" fontSize="small" />
                  <Typography variant="h6">Recurrence</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Frequency</Typography>
                    <Typography variant="body2">
                      {RecurrenceFrequencyLabels[task.recurrence.frequency] || task.recurrence.frequency}
                    </Typography>
                  </Box>
                  {task.recurrence.interval && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Interval</Typography>
                      <Typography variant="body2">Every {task.recurrence.interval} {(task.recurrence.frequency || '').toLowerCase()}</Typography>
                    </Box>
                  )}
                  {task.recurrence.next_occurrence && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Next Occurrence</Typography>
                      <Typography variant="body2">{formatDateTime(task.recurrence.next_occurrence)}</Typography>
                    </Box>
                  )}
                  {task.recurrence.end_type && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">End Type</Typography>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {task.recurrence.end_type.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  )}
                  {task.recurrence.end_date && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">End Date</Typography>
                      <Typography variant="body2">{formatDate(task.recurrence.end_date)}</Typography>
                    </Box>
                  )}
                  {task.recurrence.max_occurrences && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Max Occurrences</Typography>
                      <Typography variant="body2">{task.recurrence.max_occurrences}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TagIcon color="action" fontSize="small" />
                  <Typography variant="h6">Tags</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {task.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={typeof tag === 'string' ? tag : tag.name || tag.label}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Custom Fields */}
            {task.custom_fields && Object.keys(task.custom_fields).length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Custom Fields
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.5}>
                  {Object.entries(task.custom_fields).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {key.replace(/_/g, ' ')}
                      </Typography>
                      <Typography variant="body2">
                        {value !== null && value !== undefined ? String(value) : '-'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <TaskForm
        open={editOpen}
        onClose={handleEditClose}
        task={task}
        chatbotId={chatbotId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{task.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskDetail;
